import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, CheckCircle2, AlertCircle, Clock, Loader2 } from 'lucide-react';
import { siteConfig } from '@/config/site';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LABELS = {
  QV: 'Quick Visit (15 min)',
  HH: 'Half Hour (30 min)',
  HR: 'Full Hour (60 min)',
};

/**
 * Casa de Candy — public booking management surface.
 *
 * Reached via the one-time link in the booking confirmation email:
 *   /booking/manage/:token
 *
 * The token expires in 72h and is invalidated on successful cancellation.
 * No admin auth — all gating is server-side via the token.
 */
export function ManageBookingPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [confirm, setConfirm] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/manage/${token}`);
      if (res.ok) {
        const booking = await res.json();
        setState({ status: 'ready', booking });
      } else {
        const j = await res.json().catch(() => ({}));
        setState({ status: 'error', code: res.status, message: j.detail || 'Unable to load booking.' });
      }
    } catch (e) {
      setState({ status: 'error', code: 0, message: String(e) });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings/manage/${token}/cancel`, { method: 'POST' });
      if (res.ok) {
        const j = await res.json();
        setState({ status: 'cancelled', cancelled: j });
      } else {
        const j = await res.json().catch(() => ({}));
        setState({ status: 'error', code: res.status, message: j.detail || 'Cancellation failed.' });
      }
    } catch (e) {
      setState({ status: 'error', code: 0, message: String(e) });
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Booking | Casa de Candy</title>
      </Helmet>
      <div className="min-h-screen bg-[#07060B] flex items-center justify-center px-4 py-12" data-testid="manage-booking-page">
        <div className="w-full max-w-lg">
          <div className="flex justify-center mb-8">
            <img
              src={siteConfig.brand.logoUrl}
              alt="Casa de Candy"
              className="w-32 h-auto"
              style={{ filter: 'drop-shadow(0 0 25px rgba(139,92,255,0.6))' }}
            />
          </div>

          <div
            className="rounded-2xl border border-white/10 bg-[#0F0E1A] p-7"
            data-testid="manage-booking-card"
          >
            {state.status === 'loading' && (
              <div className="flex flex-col items-center text-[#A7A3BA] py-6" data-testid="manage-booking-loading">
                <Loader2 className="w-6 h-6 animate-spin mb-3" />
                <p className="text-sm">Verifying your link…</p>
              </div>
            )}

            {state.status === 'error' && (
              <div className="text-center py-6" data-testid="manage-booking-error">
                <AlertCircle className="w-8 h-8 mx-auto text-red-300 mb-3" />
                <h1 className="font-heading text-xl font-semibold text-[#F4F2FF] mb-2">
                  {state.code === 410 ? 'Link expired or already used' : 'Link not valid'}
                </h1>
                <p className="text-sm text-[#A7A3BA] mb-6 max-w-sm mx-auto">
                  {state.message}. If you need help with your booking, please contact us directly.
                </p>
                <Link
                  to="/client"
                  className="inline-flex items-center gap-2 text-[#FFB6D8] hover:text-white text-sm"
                  data-testid="manage-booking-back-home"
                >
                  Back to Casa de Candy
                </Link>
              </div>
            )}

            {state.status === 'cancelled' && (
              <div className="text-center py-6" data-testid="manage-booking-cancelled">
                <CheckCircle2 className="w-9 h-9 mx-auto text-emerald-300 mb-3" />
                <h1 className="font-heading text-xl font-semibold text-[#F4F2FF] mb-2">
                  Cancellation confirmed
                </h1>
                <p className="text-sm text-[#A7A3BA] mb-6 max-w-sm mx-auto">
                  Your booking has been cancelled. The team has been notified —
                  this link can no longer be used.
                </p>
                <Link
                  to="/client"
                  className="inline-flex items-center gap-2 text-[#FFB6D8] hover:text-white text-sm"
                  data-testid="manage-booking-back-home"
                >
                  Back to Casa de Candy
                </Link>
              </div>
            )}

            {state.status === 'ready' && state.booking && (
              <div data-testid="manage-booking-ready">
                <div className="flex items-center gap-2 text-[#FFB6D8] uppercase tracking-widest text-[11px] mb-2">
                  <Calendar className="w-3 h-3" />
                  Manage your booking
                </div>
                <h1 className="font-heading text-2xl font-semibold text-[#F4F2FF] mb-5">
                  {state.booking.name}
                </h1>

                <dl className="space-y-3 text-sm border border-white/5 rounded-lg p-4 bg-[#07060B]">
                  <Row label="Service">
                    {SERVICE_LABELS[state.booking.service_code] || state.booking.service_code}
                  </Row>
                  <Row label="Session type">
                    {state.booking.session_type === 'in-call' ? 'In-call' : 'Out-call'}
                  </Row>
                  <Row label="Time">
                    {state.booking.start_at || state.booking.preferred_datetime || 'TBD'}
                  </Row>
                  <Row label="Status">
                    <span className="capitalize">{state.booking.status}</span>
                  </Row>
                </dl>

                <p className="text-[11px] text-[#A7A3BA] mt-3 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  This link expires {state.booking.manage_token_expires_at?.slice(0, 16).replace('T', ' ')}.
                </p>

                <div className="mt-6 pt-6 border-t border-white/5">
                  {!confirm ? (
                    <button
                      type="button"
                      onClick={() => setConfirm(true)}
                      className="w-full px-4 py-3 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors text-sm font-medium"
                      data-testid="manage-booking-cancel-btn"
                    >
                      Cancel this booking
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-[#F4F2FF]">
                        Cancel this booking? This cannot be undone — to rebook
                        you would need to start a new request.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancel}
                          disabled={acting}
                          className="flex-1 px-4 py-3 rounded-lg bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 text-sm font-medium disabled:opacity-50"
                          data-testid="manage-booking-cancel-confirm"
                        >
                          {acting ? 'Cancelling…' : 'Yes, cancel'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirm(false)}
                          disabled={acting}
                          className="flex-1 px-4 py-3 rounded-lg border border-white/10 text-[#F4F2FF] hover:bg-white/5 text-sm font-medium"
                          data-testid="manage-booking-cancel-abort"
                        >
                          Keep it
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-[#A7A3BA] text-center mt-4">
                  Need to reschedule instead? Reply to your confirmation email
                  and the team will help you find a new time.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-[#A7A3BA] uppercase tracking-wider text-[11px] pt-0.5 min-w-[100px]">
        {label}
      </dt>
      <dd className="text-[#F4F2FF] text-right flex-1">{children}</dd>
    </div>
  );
}

export default ManageBookingPage;
